import React from "react";

import {
    Control,
    UseFormGetValues,
    UseFormSetValue,
    useWatch,
} from "react-hook-form";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import shallow from "zustand/shallow";

import Checkbox from "../../../../shared/forms/NewCheckbox";
import ReactHookInput from "../../../../shared/forms/ReactHookInput";
import { ErrorWrapper } from "../../../../shared/forms/ReactHookInput/ErrorMessage";
import { IconPredefined } from "../../../../shared/icons/Icon";
import { PredefinedIconType } from "../../../../shared/icons/Icon/Predefined";
import { iFormCardCreate } from "../Steps/CreateCardList";
import useStore, { iCreateCard, infoCards, LimitTimes } from "../store";
import LimitSelect from "./LimitSelect";

export const FormFlex = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const Row = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: start;
    width: 100%;
`;

export const RowHeadline = styled.div`
    width: 100%;
    font-weight: bold;
    color: ${({ theme }) => theme.colors.blue.toString()};
`;

export const Column = styled.div`
    display: flex;
    flex: 1;
    gap: 10px;
    align-items: center;
`;

const InlineI = styled.div`
    display: flex;
    gap: 5px;
    align-items: center;
    justify-items: center;
`;

export type iLocalError = {
    cardKey: number;
    type: string;
    message: string;
};

const hasFuelTypeOrCarWashSelected = (card: iCreateCard) => {
    if (card.services.wash) {
        return true;
    }
    return Object.values(card.fuelTypes).some((type) => type);
};

export const validate = (cards: iCreateCard[]): any[] => {
    const errors: any[] = [];
    cards.forEach((card) => {
        if (card.limits.some((limit) => limit.time === LimitTimes.none)) {
            errors.push({
                [card.key]: { limits: "Zeitraum muss ausgewählt werden." },
            });
        }
        if (!hasFuelTypeOrCarWashSelected(card)) {
            errors.push({
                [card.key]: {
                    fuelTypes:
                        "Es muss mindestens ein Kraftstoff ausgewählt werden, oder der Service: Wäsche (Pkw/Lkw).",
                },
            });
        }
    });
    return errors;
};

type iCardForm = {
    cardKey: number;
    control: Control<iFormCardCreate, any>;
    setValue: UseFormSetValue<iFormCardCreate>;
    getValues: UseFormGetValues<iFormCardCreate>;
    localErrors: iLocalError[];
    clearLocalError: (cardKey: number, type: string) => void;
    showInfoIcon?: boolean;
};
const CardForm = ({
    cardKey,
    control,
    setValue,
    getValues,
    localErrors,
    clearLocalError,
    showInfoIcon = false,
}: iCardForm) => {
    const theme = useTheme();

    const [setSelectedInfoCard] = useStore(
        (state) => [state.setSelectedInfoCard],
        shallow
    );

    const diesel = useWatch({
        control,
        name: `cards.${cardKey}.fuelTypes.diesel`,
    });

    const petrol = useWatch({
        control,
        name: `cards.${cardKey}.fuelTypes.petrol`,
    });

    const lpg = useWatch({
        control,
        name: `cards.${cardKey}.fuelTypes.lpg`,
    });

    const lng = useWatch({
        control,
        name: `cards.${cardKey}.fuelTypes.lng`,
    });

    const oil = useWatch({
        control,
        name: `cards.${cardKey}.services.oil`,
    });

    const wash = useWatch({
        control,
        name: `cards.${cardKey}.services.wash`,
    });

    const other = useWatch({
        control,
        name: `cards.${cardKey}.services.other`,
    });

    const travis = useWatch({
        control,
        name: `cards.${cardKey}.services.travis`,
    });

    const cardWithKm = useWatch({
        control,
        name: `cards.${cardKey}.cardWithKm`,
    });

    const cardWithPrint = useWatch({
        control,
        name: `cards.${cardKey}.cardWithPrint`,
    });

    const cardId = useWatch({
        control,
        name: `cards.${cardKey}.key`,
    });

    const validatePin = (pin: string) => {
        if (!pin) {
            return true;
        }

        const isNumericAndFourDigits = /^\d{4}$/.test(pin);
        /*const hasDistinctDigits = new Set(pin.split("")).size !== 1;
        const isNotSequentialOrCommon = !["1234", "0815", "0000"].includes(pin);
        const hasNoSequentialNumbers =
            !/(012|123|234|345|456|567|678|789)/.test(pin);*/
        const is0000 = pin === "0000";

        if (!isNumericAndFourDigits) {
            return "Der Pin muss 4 Zahlen lang sein.";
        }

        if (is0000) {
            return 'Der PIN-Code darf nicht "0000" sein.';
        }

        /*if (!hasDistinctDigits) {
            return "Die Pin darf keine wiederholungen haben";
        }

        if (!isNotSequentialOrCommon) {
            return "Die Pin darf nicht aufeinanderfolgend oder eine gängige PIN sein (z.B. 1111, 2222, 0815, 1234).";
        }

        if (!hasNoSequentialNumbers) {
            return "Die PIN darf keine aufeinanderfolgenden Zahlenreihen enthalten (z.B. 1234, 2345).";
        }*/

        return true;
    };

    return (
        <FormFlex>
            <Row>
                <Column>
                    <ReactHookInput
                        placeholder="Kartenbezeichnung (max. 20 Zeichen)"
                        name={`cards.${cardKey}.description`}
                        rules={{
                            required: true,
                            maxLength: 20,
                        }}
                        control={control}
                        fullWidth
                    />
                </Column>
                <Column>
                    <ReactHookInput
                        placeholder="Kostenstelle (max. 20 Zeichen)"
                        name={`cards.${cardKey}.costCentre`}
                        rules={{
                            required: false,
                            maxLength: 20,
                        }}
                        control={control}
                        fullWidth
                    />
                </Column>
                <Column>
                    <ReactHookInput
                        placeholder="PIN (Bei Freilassen zufälliger PIN)"
                        name={`cards.${cardKey}.pin`}
                        ifNullish=""
                        rules={{
                            validate: validatePin,
                            maxLength: 4,
                        }}
                        control={control}
                        fullWidth
                    />
                    <IconPredefined
                        type={PredefinedIconType.infoSmall}
                        color={theme.colors.blue.toString()}
                        css={css`
                            cursor: pointer;
                        `}
                        onClick={() => setSelectedInfoCard(infoCards[3])}
                    />
                </Column>
            </Row>
            <RowHeadline>Kraftstoffe</RowHeadline>
            {localErrors.length > 0 &&
                localErrors.some(
                    (localError) =>
                        localError.cardKey === cardId &&
                        localError.type === "fuelTypes"
                ) && (
                    <ErrorWrapper>
                        {
                            localErrors.find(
                                (localError) =>
                                    localError.cardKey === cardId &&
                                    localError.type === "fuelTypes"
                            )?.message
                        }
                    </ErrorWrapper>
                )}
            <Row>
                <Column>
                    <Checkbox
                        label="Diesel/AdBlue/HVO 100"
                        checked={diesel}
                        onClick={() => {
                            clearLocalError(cardId, "fuelTypes");
                            setValue(
                                `cards.${cardKey}.fuelTypes.diesel`,
                                !diesel
                            );
                        }}
                        withField
                        readOnly
                    />
                </Column>
                <Column>
                    <Checkbox
                        label="Superkraftstoffe"
                        checked={petrol}
                        onClick={() => {
                            clearLocalError(cardId, "fuelTypes");
                            setValue(
                                `cards.${cardKey}.fuelTypes.petrol`,
                                !petrol
                            );
                        }}
                        withField
                        readOnly
                    />
                </Column>
                <Column>
                    <Checkbox
                        label="LPG/CNG"
                        checked={lpg}
                        onClick={() => {
                            clearLocalError(cardId, "fuelTypes");
                            setValue(`cards.${cardKey}.fuelTypes.lpg`, !lpg);
                        }}
                        withField
                        readOnly
                    />
                </Column>
                <Column>
                    <InlineI>
                        <Checkbox
                            checked={lng}
                            onClick={() => {
                                clearLocalError(cardId, "fuelTypes");
                                setValue(
                                    `cards.${cardKey}.fuelTypes.lng`,
                                    !lng
                                );
                            }}
                            withField
                            readOnly
                            label="LNG"
                        />

                        {showInfoIcon && (
                            <IconPredefined
                                type={PredefinedIconType.infoSmall}
                                color={theme.colors.darkGrey.toString()}
                                hoverColor={theme.colors.blue.toString()}
                                size={"18px"}
                                css={css`
                                    margin-bottom: 0.75rem;
                                    cursor: pointer;
                                `}
                                onClick={() =>
                                    setSelectedInfoCard(infoCards[2])
                                }
                            />
                        )}
                    </InlineI>
                </Column>
            </Row>
            <RowHeadline>Services</RowHeadline>
            <Row>
                <Column>
                    <Checkbox
                        label="Öle"
                        checked={oil}
                        onClick={() =>
                            setValue(`cards.${cardKey}.services.oil`, !oil)
                        }
                        withField
                        readOnly
                    />
                </Column>
                <Column>
                    <Checkbox
                        label="Wäsche (Pkw/Lkw)"
                        checked={wash}
                        onClick={() =>
                            setValue(`cards.${cardKey}.services.wash`, !wash)
                        }
                        withField
                        readOnly
                    />
                </Column>
                <Column>
                    <InlineI>
                        <Checkbox
                            checked={other}
                            onClick={() =>
                                setValue(
                                    `cards.${cardKey}.services.other`,
                                    !other
                                )
                            }
                            label="Sonst. Fahrzeugleistungen"
                            withField
                            readOnly
                        />
                        {showInfoIcon && (
                            <IconPredefined
                                type={PredefinedIconType.infoSmall}
                                color={theme.colors.darkGrey.toString()}
                                hoverColor={theme.colors.blue.toString()}
                                size={"18px"}
                                css={css`
                                    margin-bottom: 0.75rem;
                                    cursor: pointer;
                                `}
                                onClick={() =>
                                    setSelectedInfoCard(infoCards[1])
                                }
                            />
                        )}
                    </InlineI>
                </Column>
                <Column>
                    <InlineI>
                        <Checkbox
                            checked={travis}
                            onClick={() =>
                                setValue(
                                    `cards.${cardKey}.services.travis`,
                                    !travis
                                )
                            }
                            label="Service bei TRAVIS"
                            withField
                            readOnly
                        />
                        {showInfoIcon && (
                            <IconPredefined
                                type={PredefinedIconType.infoSmall}
                                color={theme.colors.darkGrey.toString()}
                                hoverColor={theme.colors.blue.toString()}
                                size={"18px"}
                                css={css`
                                    margin-bottom: 0.75rem;
                                    cursor: pointer;
                                `}
                                onClick={() =>
                                    setSelectedInfoCard(infoCards[0])
                                }
                            />
                        )}
                    </InlineI>
                </Column>
            </Row>
            <LimitSelect
                cardKey={cardKey}
                control={control}
                setValue={setValue}
                getValues={getValues}
                localErrors={localErrors}
                clearLocalError={clearLocalError}
                cardId={cardId}
            />
            <RowHeadline>Zusätzliche Optionen</RowHeadline>
            <Row>
                <Column>
                    <Checkbox
                        label="Tankkarte mit km Eingabe"
                        checked={cardWithKm}
                        onClick={() =>
                            setValue(`cards.${cardKey}.cardWithKm`, !cardWithKm)
                        }
                        withField
                        readOnly
                    />
                </Column>
                <Column>
                    <Checkbox
                        label="Kartenbezeichnung auf Tankkarte drucken"
                        checked={cardWithPrint}
                        onClick={() =>
                            setValue(
                                `cards.${cardKey}.cardWithPrint`,
                                !cardWithPrint
                            )
                        }
                        withField
                        readOnly
                    />
                </Column>
            </Row>
            <Row>
                <Column>
                    <ReactHookInput
                        placeholder="Bemerkung..."
                        name={`cards.${cardKey}.text`}
                        rules={{
                            required: false,
                            maxLength: 100,
                        }}
                        control={control}
                        fullWidth
                    />
                </Column>
            </Row>
        </FormFlex>
    );
};

export default CardForm;
